/**
 * JSON Schema validation using ajv.
 * Returns errors as [{instancePath, message, keyword}].
 */
import Ajv from 'ajv/dist/2020';
import addFormats from 'ajv-formats';
import ajvErrors from 'ajv-errors';

const ajv = new Ajv({allErrors: true, strictTypes: false});
addFormats(ajv);
ajvErrors(ajv);
const cache = new WeakMap();

const getValidator = (schema) => {
    if (cache.has(schema)) return cache.get(schema);
    const compiled = ajv.compile(schema);
    cache.set(schema, compiled);
    return compiled;
};


const getByPath = (obj, path) => {
    let current = obj;
    for (const key of path) {
        if (current == null) return undefined;
        current = current[key];
    }
    return current;
};

const deepMergeSchema = (base, overlay) => {
    if (overlay == null) return base;
    if (base == null) return overlay;
    if (typeof base !== 'object' || typeof overlay !== 'object') return overlay;
    if (Array.isArray(overlay)) return overlay;
    const result = {...base};
    for (const [key, val] of Object.entries(overlay)) {
        result[key] = deepMergeSchema(result[key], val);
    }
    return result;
};

const resolveFieldSchema = (schema, path, data) => {
    let current = schema;
    let currentData = data;
    for (const key of path) {
        if (!current) return undefined;
        // Apply triggered dependentSchemas at this level
        if (currentData && typeof currentData === 'object' && !Array.isArray(currentData) && current.dependentSchemas) {
            for (const [depKey, depSchema] of Object.entries(current.dependentSchemas)) {
                if (depKey in currentData) {
                    current = deepMergeSchema(current, depSchema);
                }
            }
        }
        current = current.properties?.[key];
        currentData = currentData?.[key];
    }
    return current;
};

const validate = (schema, data) => {
    if (!schema || !data) return [];

    const compiled = getValidator(schema);
    compiled(data);

    const errors = [];
    const seen = new Set();

    // Map ajv errors — remap required errors from parent to child path
    for (const err of compiled.errors || []) {
        let instancePath = err.instancePath;
        let message = err.message;
        let keyword = err.keyword;

        if (err.keyword === 'required') {
            instancePath = (err.instancePath || '') + '/' + err.params.missingProperty;
            message = 'is a required property';
        } else if (err.keyword === 'errorMessage' && err.params?.errors?.[0]?.keyword === 'required') {
            const inner = err.params.errors[0];
            instancePath = (err.instancePath || '') + '/' + inner.params.missingProperty;
        }

        if (!seen.has(instancePath)) {
            seen.add(instancePath);
            errors.push({instancePath, message, keyword});
        }
    }

    // UI-specific: treat empty strings and empty arrays as missing for required fields
    // (JSON Schema required only checks property existence, not emptiness)
    const checkEmpty = (propSchema, obj, pathPrefix) => {
        const reqFields = propSchema?.required || [];
        for (const [key, fieldSchema] of Object.entries(propSchema?.properties || {})) {
            const instancePath = pathPrefix + '/' + key;
            const value = obj?.[key];

            if (fieldSchema.type === 'object' && fieldSchema.properties) {
                checkEmpty(fieldSchema, value, instancePath);
            } else if (
                fieldSchema.type === 'array'
                && fieldSchema.items?.type === 'object'
                && fieldSchema.items?.properties
                && Array.isArray(value)
            ) {
                value.forEach((item, index) => {
                    checkEmpty(fieldSchema.items, item, instancePath + '/' + index);
                });
            } else if (reqFields.includes(key) && !seen.has(instancePath)) {
                const empty = value === '' || (Array.isArray(value) && value.length === 0);
                if (empty) {
                    seen.add(instancePath);
                    errors.push({instancePath, message: 'is a required property', keyword: 'required'});
                }
            }
        }

        // Recurse into additionalProperties entries
        const addlSchema = propSchema.additionalProperties;
        if (addlSchema && typeof addlSchema === 'object' && addlSchema.properties && obj && typeof obj === 'object') {
            for (const [key, value] of Object.entries(obj)) {
                if (!propSchema.properties?.[key]) {
                    checkEmpty(addlSchema, value, pathPrefix + '/' + key);
                }
            }
        }
    };

    checkEmpty(schema, data, '');
    return errors;
};

export {validate, getByPath, resolveFieldSchema, deepMergeSchema};
